import React from "react";
import {FC} from "react";
import DtoTable from "../../components/dto-table/dto-table";
import StockListModel from "../../models/dto/stock-list-model";
import useSafeState from "../../utils/safe-state";
import {Breadcrumb, Tooltip, Button, message, Popconfirm, Modal, Select, Tag} from "antd";
import {Trans} from "@lingui/macro";
import {useAxios} from "../../utils/hooks";
import { AxiosResponse, AxiosError } from 'axios';
import {antdContext} from "../../utils/antdContext";
import { openNotification } from '../../utils/openNotification';

import {
    DeleteTwoTone,
    EditTwoTone
} from "@ant-design/icons/lib";
import ButtonWithIcon from "../../components/buttons/button-with-icon";
import {SyncOutlined} from "@ant-design/icons";

const StockListScreen : FC = () => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [loading, setLoading] = useSafeState(false);
    const [trigger, setTrigger] = useSafeState(false);
    const [statusModalVisible, setStatusModalVisible] = useSafeState(false);
    const [selectedRecord, setSelectedRecord] = useSafeState<any>(null);
    const [newStatus, setNewStatus] = useSafeState<string>('');

    const statusOptions = [
        { value: 'PRODUCED', label: <Trans>Manufactured</Trans> },
        { value: 'CAN_BE_USED', label: <Trans>Usable</Trans> },
        { value: 'LOCKED', label: <Trans>Locked</Trans> },
        { value: 'SCRAP', label: <Trans>Scrap</Trans> },
    ];

    const handleStatusChange = () => {
        if (!newStatus || !selectedRecord) {
            message.error('Please select a status');
            return;
        }

        if (axiosInstance.current == null) return;

        setLoading(true);
        axiosInstance.current
            .put("/resource/stock/change-status", null, {
                params: {
                    rollNumber: selectedRecord.rollNumber,
                    status: newStatus
                }
            })
            .then(function () {
                openNotification('customSuccess', 'Status changed successfully');
                setStatusModalVisible(false);
                setSelectedRecord(null);
                setNewStatus('');
                setTrigger(!trigger);
                setLoading(false);
            })
            .catch(function (error: any) {
                const errMessage = error && error.response && error.response.data
                    ? error.response.data
                    : 'Status change failed';
                openNotification('error', errMessage);
                setLoading(false);
            });
    };

    const showStatusModal = (record: any) => {
        setSelectedRecord(record);
        setNewStatus(record.status || '');
        setStatusModalVisible(true);
    };

    const handleDelete = (record: any) => {
        if (axiosInstance.current == null) return;
        
        const rollNumber = record.rollNumber;
        axiosInstance.current
            .delete("/resource/stock/deleterecord?rollNumber=" + encodeURIComponent(rollNumber))
            .then(function () {
                openNotification('customSuccess', 'Stock deleted successfully');
                setTrigger(!trigger);
            })
            .catch(function (error: any) {
                const errMessage = error && error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : 'Delete failed';
                openNotification('error', errMessage);
            });
    };

    const action = (record: any) => (
        <>
            <Tooltip title={<Trans>Change Status</Trans>}>
                <SyncOutlined
                    className={'ActionButtons'}
                    twoToneColor={'#1890ff'}
                    onClick={() => showStatusModal(record)}
                    style={{ marginRight: 12, cursor: 'pointer' }}
                />
            </Tooltip>
            <Tooltip title={<Trans>Remove</Trans>}>
                <Popconfirm
                    title={<Trans>Are you sure want to delete record?</Trans>}
                    onConfirm={() => handleDelete(record)}
                    okText={<Trans>Yes</Trans>}
                    cancelText={<Trans>No</Trans>}>
                    <DeleteTwoTone className={'ActionButtons'} twoToneColor={'red'}/>
                </Popconfirm>
            </Tooltip>
        </>
    );

    const getTransStatus = (msg: any) => {

        let label: React.ReactNode = selectedRecord.status;
        switch (selectedRecord.status) {
            case 'PRODUCED':
                label = <Trans>Manufactured</Trans>;
                break;
            case 'CAN_BE_USED':
                label = <Trans>Usable</Trans>;
                break;
            case 'LOCKED':
                label = <Trans>Locked</Trans>;
                break;
            case 'SCRAP':
                label = <Trans>Scrap</Trans>;
                break;
            case 'TAKEN_BACK_FROM_PRODUCTION':
                label = <Trans>Returned from production</Trans>;
                break;
        }
        return label;
    }

    const actions = {
        width: 110,
        render: (text: string, record: any) => action(record),
        title: '',
        fixed: 'right',
        align: 'center'
    };

    return (
        <>
            <Breadcrumb style={{ marginBottom: 10 }}>
                <Breadcrumb.Item><Trans id={'Homepage'}/></Breadcrumb.Item>
                <Breadcrumb.Item><Trans id={'Stock list'}></Trans></Breadcrumb.Item>
            </Breadcrumb>
            <DtoTable
                model={StockListModel}
                action={actions}
                trigger={trigger}
                allowExport={true}
                allowRefreshButton={true}
                pageSize={100}
                scroll={{ x: 1300, y: "65vh" }}
            />
            
            <Modal
                title={<Trans>Change Stock Status</Trans>}
                visible={statusModalVisible}
                onOk={handleStatusChange}
                onCancel={() => {
                    setStatusModalVisible(false);
                    setSelectedRecord(null);
                    setNewStatus('');
                }}
                okText="Save"
                cancelText="Cancel"
                confirmLoading={loading}
            >
                <div style={{ marginBottom: 16 }}>
                    <div><Trans>Roll Number</Trans>: <strong>{selectedRecord && selectedRecord.rollNumber}</strong></div>
                    <div>
                        <Trans>Current Status</Trans>:
                        <strong> {selectedRecord && getTransStatus(selectedRecord.status)}</strong>
                    </div>
                </div>
                <Select
                    style={{ width: '100%' }}
                    placeholder="Select new status"
                    value={newStatus}
                    onChange={setNewStatus}
                    options={statusOptions}
                />
            </Modal>
        </>
    );
}

export default StockListScreen;
